import React from "react";
import { Callout, Code, CTA, Lead, P, Section } from "../../../../blog/components";

export const title = "不包装进程，如何观察六种编程 Agent CLI";
export const excerpt =
  "Claude Code、Codex、Cursor、Grok、OpenCode 和 Pi 报告活动的方式各不相同。我们把它们的原生 hook 统一成了一个简单的生命周期。";
export const heroAlt = "从岩石山坡望向开阔的绿色平原";
export const readingTime = "阅读约 4 分钟";

export default function Post() {
  return (
    <>
      <Lead>
        终端知道一个进程是否存在，但它不知道编程 Agent 正在工作、等待批准，还是已经完成当前回合。
        同时打开六个 Agent 时，这个区别就很重要。
      </Lead>

      <P>
        Cate 支持 Claude Code、Codex、Cursor、Grok、OpenCode 和 Pi。我们希望每个终端都能显示相同的基本生命周期，
        同时不改变这些工具原本的启动方式：
      </P>

      <Code>{`工作中
  -> 等待权限或输入
  -> 已结束`}</Code>

      <P>
        解析终端输出并不可靠。加载动画会变，文本会被重绘，而一个安静的进程可能正在思考、运行工具，也可能被权限提示卡住。
        进程树只能告诉我们 CLI 是否还活着。
      </P>

      <Section title="把六种 hook API 变成一个事件流">
        <P>
          每个 CLI 都提供结构化的集成入口，但命名完全不同。Claude Code 有 <code>UserPromptSubmit</code>、
          <code>Notification</code> 和 <code>Stop</code>。OpenCode 会发送 <code>session.status</code>、
          <code>permission.asked</code> 和 <code>session.idle</code>。Pi 则提供进程内扩展 API。
        </P>

        <P>Cate 把它们映射成六个内部事件：</P>

        <Code>{`session-start
turn-start
permission-wait
turn-resume
turn-end
session-end`}</Code>

        <P>
          一个很小的 hook bridge 会把事件发送到 Cate runtime 的本地端点。终端 ID 用来找到正确的面板，每个终端也有独立的 token。
          对于远程终端，这个端点直接运行在远程机器上。
        </P>

        <P>
          Cate 不会包装 Agent 进程。运行 <code>codex</code> 仍然会直接启动 Codex。只有 CLI 调用原生 hook 时，bridge 才会运行。
          如果 hook 配置失败，Cate 会照常打开一个普通 shell。
        </P>

        <Callout>
          这些适配器不会假装所有 CLI 都能提供相同的信息。Cursor 没有专门的权限事件，所以 Cate 不会猜测。
          在它目前的 hook API 中，即将执行的命令和等待批准的命令看起来完全一样。
        </Callout>
      </Section>

      <Section title="回合状态和进程状态不是一回事">
        <P>
          <code>turn-end</code> 表示 Agent 正在等待下一条提示，并不表示进程已经结束。崩溃和手动退出需要单独处理。
        </P>

        <P>
          第一个通过认证的 hook 请求会给 Cate 一个可跟踪的进程。对于命令式 hook，runtime 会从 bridge 进程向上查找，直到找到对应的 Agent。
          进程内插件可以直接报告 PID。之后，一个轻量的存活检查会在进程消失时把终端标记为已结束。
        </P>
      </Section>

      <Section title="会话 ID 需要先被证明可用">
        <P>
          适配器还会收集 Agent 的会话 ID。Cate 将它和终端一起保存，并在应用重启后使用各 CLI 自己的恢复命令。
          Claude Code 可能在记录文件创建之前就发送第一个会话事件，所以我们会等到第一个回合事件，再把该会话标记为可恢复。
        </P>

        <P>
          恢复只尝试一次。Cate 在新的 shell 中输入恢复命令，然后立刻清除旧标记。恢复成功后，新 hook 会再次写入标记。
          如果 ID 已失效，CLI 会显示自己的错误，下一次重启则只打开普通 shell，不会一直重试。
        </P>
      </Section>

      <Section title="通知跟随状态变化">
        <P>
          大量工具调用会产生很多事件，因此通知只由状态变化触发。工作中变为等待时，可以显示被阻塞的命令。
          重复的等待事件不会重复通知，点击通知会直接打开对应终端。
        </P>

        <P>
          对外的模型仍然很小：工作中、等待、已结束。复杂度留在下面，由六种不断变化的 hook API、进程存活状态和可恢复会话共同承担。
        </P>
      </Section>

      <CTA label="查看 Cate 源码" href="https://github.com/0-AI-UG/cate" />
    </>
  );
}
